import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { FriendListPlayerParams } from '../components/FriendListItem';

// Row geometry, matched to FriendListItem (offsetX 10, width 498) so the
// suggestion chrome aligns with the player tiles above/below it in the scroll.
const ROW_OFFSET_X = 10;
const ROW_WIDTH = 498;
// Header row fits the ribbon banner plus a little vertical padding.
const HEADER_HEIGHT = 50;
// Separator row: a thin divider line shown between the user's existing list
// (friends or requests) and the "You may know" suggestions section.
const SEPARATOR_HEIGHT = 20;

/** "You may know" ribbon header row. */
export type SuggestionsHeaderParams = {
  isSuggestionsHeader: true;
  label: string;
};

/** Thin divider between the existing list and the suggestions section. */
export type SuggestionsDividerParams = {
  isSuggestionsDivider: true;
};

/**
 * Every row type the suggestion-augmented Friend List scrolls render: the
 * existing player tiles (friends / requests / suggestion candidates) plus the
 * section's own chrome rows.
 */
export type FriendSuggestionsListItem =
  | FriendListPlayerParams
  | SuggestionsHeaderParams
  | SuggestionsDividerParams;

/** The red "You may know" ribbon banner with a centered white label. */
class SuggestionsRibbonHeader extends View {
  constructor(opts: { superview: View; label: string }) {
    super({
      superview: opts.superview,
      offsetX: ROW_OFFSET_X,
      width: ROW_WIDTH,
      height: HEADER_HEIGHT,
    });

    const banner = new ImageScaleView({
      superview: this,
      image: 'assets/ui/friends/suggestions_ribbon.png',
      scaleMethod: 'stretch',
      width: 331.5,
      height: 50,
      x: this.style.width / 2,
      y: HEADER_HEIGHT / 2,
      centerOnOrigin: true,
    });

    new LangBitmapFontTextView({
      superview: banner,
      width: banner.style.width - 40,
      height: banner.style.height,
      x: banner.style.width / 2,
      y: banner.style.height / 2 - 6,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 23,
      font: 'Title',
      color: '#FFFFFF',
      canHandleEvents: false,
      localeText: () => opts.label,
    });
  }
}

/** A thin horizontal divider line, centered in its row. */
class SuggestionsDivider extends View {
  constructor(opts: { superview: View }) {
    super({
      superview: opts.superview,
      offsetX: ROW_OFFSET_X,
      width: ROW_WIDTH,
      height: SEPARATOR_HEIGHT,
    });

    new View({
      superview: this,
      backgroundColor: '#e6a357',
      width: 478,
      height: 4,
      x: this.style.width / 2,
      y: SEPARATOR_HEIGHT / 2,
      centerOnOrigin: true,
    });
  }
}

/**
 * Build the view for a suggestion chrome row (ribbon header / divider). Returns
 * `null` for player rows so the caller falls through to its own `FriendListItem`
 * construction. Lets the two Friend List tabs render the mixed list through a
 * single non-recycled scroll without the shared `FriendListItem` needing to know
 * about suggestion-specific variants.
 */
export function createSuggestionChromeView(
  superview: View,
  data: FriendSuggestionsListItem,
): View | null {
  if ('isSuggestionsHeader' in data) {
    return new SuggestionsRibbonHeader({ superview, label: data.label });
  }
  if ('isSuggestionsDivider' in data) {
    return new SuggestionsDivider({ superview });
  }
  return null;
}
